import clsx from "clsx";
import { Check, CircleHelp, Compass, Link2, Link2Off, Redo2, Route, Trash2, Undo2 } from "lucide-react";
import { useId, useState } from "react";

import { Checkbox } from "../../components/Checkbox";
import { Disclosure } from "../../components/panel";
import { useMediaQuery } from "../../components/useMediaQuery";
import { useLastDefined } from "../../components/presence";
import { Button, IconButton, type IconButtonSize, Kbd, KbdHint, redoShortcut, shortcut } from "../../components/ui";
import { t, tc } from "../../i18n/i18n";
import { useUi } from "../../state/ui";
import { useHistoryState } from "../history";
import { useLineLabel } from "../lineNames";
import type { BannerLayout } from "./freeBands";
import { useGestureState } from "./gestureState";
import { useOrientationView } from "./orientationView";
import { useApplyOrientation } from "./useApplyOrientation";
import { useManoeuvre } from "./useManoeuvre";
import { runHistoryStep } from "./useManoeuvreGestures";
import { useOrientation } from "./useOrientation";
import { readOnlyText, useDossierLock } from "../../sharing/access";

/** Boutons ↶ ↷ de l'historique de la carte (docs/14 §2.8) : dans le bandeau Manœuvre pendant l'édition, dans la
 *  barre basse sinon. Ils suivent l'état de l'historique (rien à annuler : grisés) et nomment l'action visée.
 *  `size` : taille des `IconButton` (`sm` dans la barre d'état et le bandeau minimal) ; `className` : marges. */
export function HistoryButtons({ size = "md", className }: { size?: IconButtonSize; className?: string }) {
  const canUndo = useHistoryState((s) => s.canUndo);
  const canRedo = useHistoryState((s) => s.canRedo);
  const undoLabel = useHistoryState((s) => s.undoLabel);
  const redoLabel = useHistoryState((s) => s.redoLabel);
  return (
    <>
      <IconButton
        size={size}
        className={className}
        aria-label={tc("défaire", "Annuler")}
        aria-keyshortcuts="Meta+Z Control+Z"
        title={canUndo && undoLabel ? t("Annuler : {action} ({touche})", { action: undoLabel, touche: shortcut("Z") }) : t("Annuler ({touche})", { touche: shortcut("Z") })}
        disabled={!canUndo}
        onClick={() => runHistoryStep("undo")}
      >
        <Undo2 size={14} />
      </IconButton>
      <IconButton
        size={size}
        className={className}
        aria-label={tc("refaire", "Rétablir")}
        aria-keyshortcuts="Meta+Shift+Z Control+Shift+Z Control+Y"
        title={canRedo && redoLabel ? t("Rétablir : {action} ({touche})", { action: redoLabel, touche: redoShortcut() }) : t("Rétablir ({touche})", { touche: redoShortcut() })}
        disabled={!canRedo}
        onClick={() => runHistoryStep("redo")}
      >
        <Redo2 size={14} />
      </IconButton>
    </>
  );
}

/** Bandeau du mode édition du fuseau, en haut de la carte (sur le modèle du bandeau de tracé) : la consigne du geste
 *  avec ses touches, ↶ ↷, « Garder la largeur », « Retirer le sommet » quand un sommet est sélectionné (il
 *  remplace Suppr au doigt) et « Terminé ». Dossier non enregistré : la consigne désigne l'enregistrement. La zone
 *  `aria-live` annonce le sommet atteint au clavier (Tab).
 *
 *  `layout` (`freeBands.bannerPlace`, selon la bande libre entre les panneaux ; le bandeau ne passe jamais sous un
 *  panneau) :
 *  - « full » : consigne et boutons en une rangée ;
 *  - « compact » : titre et boutons sur une ou deux lignes, « Retirer » en icône (nom accessible et bulle),
 *    consigne repliée derrière « ? » — sauf dossier non enregistré, où elle reste affichée puisqu'elle désigne
 *    l'action attendue ;
 *  - « mini » : icônes seules (titre, « Garder la largeur » et « Terminé » compris, chacune nommée et bullée), sur
 *    autant de rangées que la bande l'impose ; même consigne repliée. */
export function ManoeuvreBanner({
  canEdit,
  onRemove,
  onDone,
  layout = "full",
}: {
  canEdit: boolean;
  onRemove: () => void;
  onDone: () => void;
  layout?: BannerLayout;
}) {
  const compact = layout !== "full";
  const mini = layout === "mini";
  const keepWidth = useManoeuvre((s) => s.keepWidth);
  const setKeepWidth = useManoeuvre((s) => s.setKeepWidth);
  const selected = useManoeuvre((s) => s.selected);
  const selectedLine = useManoeuvre((s) => s.selectedLine);
  const announcement = useGestureState((s) => s.announcement);
  const requestSave = useUi((s) => s.requestSave);
  const dossier = useDossierLock();
  // Ligne de la ZO sélectionnée (fiche ouverte) : la consigne dit comment la glisser et l'incliner (docs/15 §4.3).
  const measureSelected = useUi((s) => s.selection?.layer === "measure");
  const lineLabel = useLineLabel();
  const coarse = useMediaQuery("(pointer: coarse)");
  const [hintOpen, setHintOpen] = useState(false);
  const hintId = useId();
  const removable = selected?.kind === "vertex" || selected?.kind === "limaBend" ? selected : null;
  // Libellé gardé pendant la sortie du bouton (l'emplacement se referme en douceur).
  const lastRemovable = useLastDefined(removable);
  const removeLabel = lastRemovable?.kind === "limaBend" ? t("Retirer la courbure") : t("Retirer le sommet");

  let hint;
  if (dossier.locked) {
    // Dossier en lecture seule pour le compte (docs/17 §3.2) : rien à enregistrer, le texte du bandeau.
    hint = readOnlyText(dossier.ownerName);
  } else if (!canEdit) {
    hint = (
      <>
        {t("Enregistrez le dossier pour modifier le fuseau.")}{" "}
        <Button variant="link" onClick={() => requestSave(true)}>
          {t("Enregistrer")}
        </Button>
      </>
    );
  } else if (selectedLine) {
    hint = (
      <>
        {t("Glissez {line} le long du fuseau ; ses extrémités l'inclinent.", { line: lineLabel(selectedLine) })}{" "}
        {coarse
          ? t("Glissez un rond en tirets pour la courber.")
          : t("Glissez un rond en tirets pour la courber ; une courbure sélectionnée se retire avec")}
        {!coarse && (
          <KbdHint>
            {" "}
            <Kbd>⌫</Kbd>.
          </KbdHint>
        )}
      </>
    );
  } else if (measureSelected) {
    hint = <>{t("Glissez une ligne de la ZO le long du fuseau ; ses extrémités l'inclinent.")}</>;
  } else if (coarse) {
    // Les LIMA se retouchent dans la même session que les limites (demande d'Alexis du 04/10/2026) : la consigne
    // le dit, la LIMA touchée montre ensuite ses propres gestes.
    hint = (
      <>
        {t("Glissez un rond blanc pour déplacer le fuseau à cet endroit. Touchez deux fois un sommet pour le retirer.")}{" "}
        {t("Touchez une LIMA pour la retoucher.")}
      </>
    );
  } else {
    hint = (
      <>
        {t("Glissez un rond blanc pour déplacer le fuseau à cet endroit.")}{" "}
        <KbdHint className="whitespace-nowrap">
          <Kbd>⌥</Kbd> {t("une seule limite")}
        </KbdHint>{" "}
        ·{" "}
        <KbdHint className="whitespace-nowrap">
          <Kbd>⇧</Kbd> {t("décalage latéral")}
        </KbdHint>{" "}
        · {t("double-clic retire un sommet.")} {t("Cliquez sur une LIMA pour la retoucher.")}
      </>
    );
  }

  const keepWidthTitle = t("Glisser un sommet déplace aussi son vis-à-vis : la largeur ne change pas");
  const keepWidthSwitch = mini ? (
    <IconButton
      role="switch"
      aria-checked={keepWidth}
      aria-label={t("Garder la largeur")}
      title={t("Garder la largeur : glisser un sommet déplace aussi son vis-à-vis")}
      active={keepWidth}
      onClick={() => setKeepWidth(!keepWidth)}
      size="sm"
      className="shrink-0"
    >
      {keepWidth ? <Link2 size={14} /> : <Link2Off size={14} />}
    </IconButton>
  ) : (
    // Case à cocher partagée, dans une rangée de 32 px comme les boutons voisins.
    <span className="flex h-8 shrink-0 items-center px-2">
      <Checkbox
        checked={keepWidth}
        onChange={setKeepWidth}
        title={keepWidthTitle}
        label={t("Garder la largeur")}
        className="whitespace-nowrap"
      />
    </span>
  );

  // « Retirer le sommet » : son emplacement s'ouvre et se referme en largeur (la rangée ne saute pas).
  const removeSlot = (
    <div
      className={clsx(
        "flex shrink-0 overflow-hidden transition-[max-width,opacity] duration-(--gx-enter) ease-out motion-reduce:transition-none",
        removable ? (compact ? "max-w-10 opacity-100" : "max-w-48 opacity-100") : "max-w-0 opacity-0",
        mini && !removable && "-ml-1", // emplacement refermé : pas d'écart double dans la rangée
      )}
      inert={!removable}
    >
      {compact ? (
        <IconButton
          onClick={onRemove}
          aria-label={removeLabel}
          title={t("{action} (Suppr)", { action: removeLabel })}
          size="sm"
          className="shrink-0"
        >
          <Trash2 size={14} />
        </IconButton>
      ) : (
        <Button variant="secondary" size="sm" onClick={onRemove} title={t("Retirer (Suppr)")} className="shrink-0 whitespace-nowrap">
          <Trash2 size={14} />
          {removeLabel}
        </Button>
      )}
    </div>
  );

  const doneButton = mini ? (
    <IconButton variant="primary" size="sm" data-attention="manoeuvre-done" onClick={onDone} aria-label={t("Terminé")} title={t("Quitter l'édition du fuseau (Échap)")}>
      <Check size={14} />
    </IconButton>
  ) : (
    <Button variant="primary" size="sm" data-attention="manoeuvre-done" onClick={onDone} title={t("Quitter l'édition du fuseau (Échap)")} className="ml-1 whitespace-nowrap">
      <Check size={14} /> {t("Terminé")}
    </Button>
  );

  const live = (
    <div aria-live="polite" className="sr-only">
      {announcement}
    </div>
  );

  // Consigne dépliée : sur demande (« ? »), ou d'office quand elle désigne l'action attendue (enregistrer).
  const showHint = !canEdit || hintOpen;
  const helpToggle = canEdit && (
    <IconButton
      aria-expanded={hintOpen}
      aria-controls={hintId}
      aria-label={hintOpen ? t("Masquer la consigne") : t("Afficher la consigne")}
      title={hintOpen ? t("Masquer la consigne") : t("Afficher la consigne")}
      active={hintOpen}
      onClick={() => setHintOpen((open) => !open)}
      size="sm"
      className="shrink-0"
    >
      <CircleHelp size={14} />
    </IconButton>
  );
  // Consigne : elle s'ouvre et se referme en hauteur, jamais d'un coup ; elle passe à la ligne.
  const foldedHint = (
    <Disclosure open={showHint} id={hintId}>
      <div className="pt-1 pr-1.5 pb-0.5 text-meta leading-snug text-ink-muted">{hint}</div>
    </Disclosure>
  );

  if (mini) {
    return (
      <div
        role="region"
        aria-label={t("Modifier le fuseau")}
        className="pointer-events-auto flex max-w-full flex-col rounded-xl border border-line bg-surface/95 p-1 shadow-float backdrop-blur"
      >
        <div className="flex flex-wrap items-center justify-center gap-1">
          <span className="inline-flex size-7 shrink-0 items-center justify-center text-ink-soft" title={t("Modifier le fuseau")}>
            <Route size={16} aria-hidden />
          </span>
          {helpToggle}
          <HistoryButtons size="sm" className="shrink-0" />
          {keepWidthSwitch}
          {removeSlot}
          {doneButton}
        </div>
        {foldedHint}
        {live}
      </div>
    );
  }

  if (compact) {
    return (
      <div
        role="region"
        aria-label={t("Modifier le fuseau")}
        className="pointer-events-auto flex max-w-full flex-col rounded-xl border border-line bg-surface/95 py-1.5 pr-1.5 pl-3 shadow-float backdrop-blur"
      >
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <Route size={16} className="shrink-0 text-ink-soft" aria-hidden />
            <div className="text-ui font-semibold whitespace-nowrap text-ink">{t("Modifier le fuseau")}</div>
            {helpToggle}
          </div>
          <div className="ml-auto flex max-w-full flex-wrap items-center justify-end gap-1">
            <HistoryButtons size="sm" />
            <div className="mx-0.5 h-5 w-px bg-line" aria-hidden />
            {keepWidthSwitch}
            {removeSlot}
            {doneButton}
          </div>
        </div>
        {foldedHint}
        {live}
      </div>
    );
  }

  return (
    <div
      role="region"
      aria-label={t("Modifier le fuseau")}
      className="pointer-events-auto flex max-w-[min(820px,calc(100vw-2rem))] flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-line bg-surface/95 py-2 pr-2 pl-3 shadow-float backdrop-blur"
    >
      <div className="flex min-w-[220px] flex-1 items-center gap-3">
        <Route size={16} className="shrink-0 text-ink-soft" aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="text-ui font-semibold text-ink">{t("Modifier le fuseau")}</div>
          <div className="text-meta leading-snug text-ink-muted">{hint}</div>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <HistoryButtons size="sm" />
        <div className="mx-1 h-5 w-px bg-line" aria-hidden />
        {keepWidthSwitch}
        {removeSlot}
        {doneButton}
      </div>
      {live}
    </div>
  );
}

/** Bandeau du mode « Orienter la manœuvre » (docs/14 §2.14), variante du bandeau Manœuvre : titre, consigne (au doigt,
 *  la sienne), touches, Appliquer et Annuler ; ↶ ↷ masqués (⌘Z vaut Échap pendant le mode). Mêmes dispositions que
 *  le bandeau du fuseau (`layout`). */
export function OrientationBanner({ layout = "full" }: { layout?: BannerLayout }) {
  const compact = layout !== "full";
  const mini = layout === "mini";
  const coarse = useMediaQuery("(pointer: coarse)");
  const view = useOrientationView();
  const pending = useOrientation((s) => s.pending);
  const cancel = useOrientation((s) => s.cancel);
  const { apply } = useApplyOrientation();
  const [hintOpen, setHintOpen] = useState(false);
  const hintId = useId();
  const blocked = !pending && (!view || view.unchanged || Boolean(view.reason));
  const title = t("Sens de la manœuvre");
  const hint = coarse ? (
    <>{t("Faites tourner la flèche du doigt.")}</>
  ) : (
    <>
      {t("Faites tourner la flèche : elle va des amis vers l'ennemi.")}{" "}
      <KbdHint className="whitespace-nowrap">{t("← → 1° · ⇧ 10° · Entrée appliquer · Échap annuler")}</KbdHint>
    </>
  );
  const buttons = (
    <div className="flex shrink-0 items-center gap-1">
      <Button variant="ghost" size="sm" onClick={cancel} disabled={pending} title={t("Annuler (Échap)")}>
        {t("Annuler")}
      </Button>
      <Button
        variant="primary"
        size="sm"
        disabled={pending}
        blocked={blocked}
        onBlocked={() => apply(view)}
        onClick={() => apply(view)}
        title={t("Appliquer (Entrée)")}
        className="whitespace-nowrap"
      >
        <Check size={14} /> {mini ? null : t("Appliquer")}
      </Button>
    </div>
  );
  if (compact) {
    const helpToggle = (
      <IconButton
        aria-expanded={hintOpen}
        aria-controls={hintId}
        aria-label={hintOpen ? t("Masquer la consigne") : t("Afficher la consigne")}
        title={hintOpen ? t("Masquer la consigne") : t("Afficher la consigne")}
        active={hintOpen}
        onClick={() => setHintOpen((open) => !open)}
        size="sm"
        className="shrink-0"
      >
        <CircleHelp size={14} />
      </IconButton>
    );
    return (
      <div
        role="region"
        aria-label={title}
        className={clsx(
          "pointer-events-auto flex max-w-full flex-col rounded-xl border border-line bg-surface/95 shadow-float backdrop-blur",
          mini ? "p-1" : "py-1.5 pr-1.5 pl-3",
        )}
      >
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <Compass size={16} className="shrink-0 text-ink-soft" aria-hidden />
            {!mini && <div className="text-ui font-semibold whitespace-nowrap text-ink">{title}</div>}
            {helpToggle}
          </div>
          <div className="ml-auto">{buttons}</div>
        </div>
        <Disclosure open={hintOpen} id={hintId}>
          <div className="pt-1 pr-1.5 pb-0.5 text-meta leading-snug text-ink-muted">{hint}</div>
        </Disclosure>
      </div>
    );
  }
  return (
    <div
      role="region"
      aria-label={title}
      className="pointer-events-auto flex max-w-[min(820px,calc(100vw-2rem))] flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-line bg-surface/95 py-2 pr-2 pl-3 shadow-float backdrop-blur"
    >
      <div className="flex min-w-[220px] flex-1 items-center gap-3">
        <Compass size={16} className="shrink-0 text-ink-soft" aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="text-ui font-semibold text-ink">{title}</div>
          <div className="text-meta leading-snug text-ink-muted">{hint}</div>
        </div>
      </div>
      {buttons}
    </div>
  );
}
